import { Link } from "react-router-dom";
import { Mail, CheckCircle2, AlertTriangle, Plus } from "lucide-react";

export function RecipientsPreview({ team, talent, crew, clients, onOpenEmail }) {
  const teamById = Object.fromEntries(team.map((m) => [m.id, m]));
  const assigned = [
    ...talent.map((t) => ({ ...teamById[t.team_id], _type: "talent" })),
    ...crew.map((c) => ({ ...teamById[c.team_id], _type: "crew" })),
    ...(clients || []).map((c) => ({ ...teamById[c.team_id], _type: "client" })),
  ].filter((p) => p && p.id);

  const seen = new Set();
  const unique = assigned.filter((p) => {
    if (seen.has(p.id)) return false;
    seen.add(p.id);
    return true;
  });

  if (unique.length === 0) {
    return (
      <div className="rounded-md border border-zinc-800 bg-zinc-900/60 p-5">
        <div className="flex items-center gap-2 mb-1">
          <Mail className="w-4 h-4 text-amber-500" />
          <div className="text-overline text-zinc-400">Email Recipients</div>
        </div>
        <div className="text-sm text-zinc-500">
          Assign talent or crew below and their emails will appear here.
        </div>
      </div>
    );
  }

  const withEmail = unique.filter((p) => p.email);
  const withoutEmail = unique.filter((p) => !p.email);

  return (
    <div
      data-testid="recipients-preview"
      className="rounded-md border border-zinc-800 bg-zinc-900/60 p-5"
    >
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-amber-500" />
          <div className="text-overline text-zinc-400">
            Email Recipients
          </div>
          <span className="text-xs font-mono text-zinc-500">
            · {withEmail.length} of {unique.length}
          </span>
        </div>
        <button
          data-testid="open-email-from-preview"
          onClick={onOpenEmail}
          disabled={withEmail.length === 0}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-amber-500 text-black hover:bg-amber-400 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Mail className="w-3 h-3" /> Send Call Sheet
        </button>
      </div>

      {withEmail.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {withEmail.map((p) => (
            <div
              key={p.id}
              data-testid={`recipient-chip-${p.id}`}
              className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-zinc-950 border border-zinc-800 text-xs"
            >
              <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" />
              <span className="text-zinc-200 font-medium">{p.name}</span>
              <span className="text-zinc-500 font-mono">{p.email}</span>
            </div>
          ))}
        </div>
      )}

      {withoutEmail.length > 0 && (
        <div className="border-t border-zinc-800/60 pt-3 mt-1">
          <div className="flex items-center gap-2 text-xs text-amber-400 mb-2">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span className="font-medium">
              {withoutEmail.length} {withoutEmail.length === 1 ? "person has" : "people have"} no email on file
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {withoutEmail.map((p) => (
              <Link
                key={p.id}
                to="/team"
                data-testid={`missing-email-${p.id}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-xs text-amber-300 hover:bg-amber-500/20 transition-colors"
                title="Click to add an email in Team"
              >
                {p.name}
                <Plus className="w-3 h-3" />
              </Link>
            ))}
          </div>
          <div className="text-[11px] text-zinc-500 mt-2">
            Click any name to add an email in <strong>Team</strong>.
          </div>
        </div>
      )}
    </div>
  );
}
